// Login-method + Google-SSO gating helpers — the pure decision logic behind the
// Google Workspace SSO rollout (docs/google-sso-plan.md §3–§6).
//
// INERT for now: nothing imports these yet. Phases 3–5 wire them into Login.jsx
// (which path to show / call), the useAuthSession + portal.jsx restore checks, and
// the MFA gate. Kept pure + dependency-free (enums only — no Supabase, no DOM) so
// they're fully unit-tested in isolation, mirroring src/utils/mfa.ts.

import { LOGIN_METHODS, type LoginMethod } from "./enums";

// The Workspace domain every authorised Zephyr staff member signs in under. Shelter
// caseworkers never have an address here, which is what makes the domain-adaptive
// login screen reliable ("show the Google path only for staff" — §3/§4).
export const STAFF_EMAIL_DOMAIN = "zephyreducation.com.au";

// True when an email belongs to the Zephyr staff Workspace domain. Case-insensitive,
// trims whitespace, and requires an EXACT domain match — so a lookalike like
// "evil-zephyreducation.com.au" or a subdomain like "x.zephyreducation.com.au" do
// NOT match. Missing / malformed (no "@") emails are not staff.
export function isStaffEmailDomain(email: string | null | undefined): boolean {
  if (!email) return false;
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domain === STAFF_EMAIL_DOMAIN;
}

// Normalise a stored login_method to a known value; anything missing or unrecognised
// falls back to PASSWORD — matches the DB column default and the "nothing changes
// until switched on" posture.
export function normaliseLoginMethod(method: string | null | undefined): LoginMethod {
  if (method === LOGIN_METHODS.GOOGLE) return LOGIN_METHODS.GOOGLE;
  if (method === LOGIN_METHODS.EITHER) return LOGIN_METHODS.EITHER;
  return LOGIN_METHODS.PASSWORD;
}

// Supabase records the auth provider on the session user (app_metadata.provider):
// "google" for an OAuth sign-in, "email" for password. Only an explicit "google" is
// the Google path; everything else (email, missing) is the password path.
export type SessionAuthKind = "google" | "password";
export function sessionAuthKind(provider: string | null | undefined): SessionAuthKind {
  return provider === "google" ? "google" : "password";
}

// Is a session that arrived via `provider` allowed for a user whose method is
// `loginMethod`? This is the server-side enforcement behind the domain-adaptive
// screen (§5 "Login-method check"):
//   password -> only the password path (a Google session is rejected)
//   google   -> only the Google path  (a password session is rejected)
//   either   -> both paths work (the transition state used during the cutover)
export function isSessionAllowedForMethod(
  loginMethod: string | null | undefined,
  provider: string | null | undefined
): boolean {
  const method = normaliseLoginMethod(loginMethod);
  const kind = sessionAuthKind(provider);
  if (method === LOGIN_METHODS.EITHER) return true;
  if (method === LOGIN_METHODS.GOOGLE) return kind === "google";
  return kind === "password"; // PASSWORD
}

// Should the portal's own TOTP gate be SKIPPED for this session? Only when the
// session came in via Google AND the user's method permits Google (`google` or
// `either`) — then Google Workspace's enforced 2FA is the second factor and the
// portal authenticator is redundant (§4 table + §6). A password-path session — including
// an `either` user signing in with their password — keeps the portal TOTP gate.
//
// Note: §6's prose says "method is google"; the §4 behaviour table is broader — an
// `either` user who signs in via Google has used Google's 2FA for THAT session, so
// the portal TOTP is redundant for it too. We implement the §4-table behaviour;
// confirm at Phase 4 wiring.
export function shouldBypassPortalMfa(
  loginMethod: string | null | undefined,
  provider: string | null | undefined
): boolean {
  if (sessionAuthKind(provider) !== "google") return false;
  const method = normaliseLoginMethod(loginMethod);
  return method === LOGIN_METHODS.GOOGLE || method === LOGIN_METHODS.EITHER;
}

// Decode a JWT payload (base64url, no signature check — we only read public claims
// the session already trusts). Returns {} on any malformation.
function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) return {};
  const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "===".slice((b64.length + 3) % 4);
  return JSON.parse(atob(padded));
}

// How did the CURRENT session authenticate? Read from the access-token JWT's `amr`
// (authentication methods reference) claim, which reflects THIS session — unlike
// `app_metadata.provider`, which is the account's PRIMARY provider and goes stale
// once a Google identity is linked to an email account (it would mislabel a real
// Google session as "email", or break a password login after linking). A social
// (Google) sign-in records method "oauth"; password records "password". Returns the
// value `sessionAuthKind` expects: "google" for an OAuth session, else "email".
// Defaults to "email" (the safe, password path) on any decode failure.
export function providerFromAccessToken(accessToken: string | null | undefined): string {
  if (!accessToken) return "email";
  try {
    const payload = decodeJwtPayload(accessToken);
    const amr = Array.isArray(payload.amr) ? (payload.amr as { method?: string }[]) : [];
    return amr.some((e) => e?.method === "oauth") ? "google" : "email";
  } catch {
    return "email";
  }
}
